import { Link } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import type { OnboardingReason } from '@/api/types';

/** The reason requests are blocked for the whole session (READY when unknown - an older server never blocks). */
export function useOnboardingReason(): OnboardingReason {
  const { state } = useAuth();
  return state.status === 'authenticated' ? state.self.onboarding?.reason ?? 'READY' : 'READY';
}

/**
 * A calm inline notice that says, in words, why requests are blocked and what the next step is. It never
 * shows a code. Nothing renders when the account is ready.
 */
export function OnboardingNotice({ reason, id = 'onboarding-notice' }: { reason?: OnboardingReason; id?: string }): JSX.Element | null {
  const { t } = useLanguage();
  const session = useOnboardingReason();
  const r = reason ?? session;
  if (r === 'READY') return null;
  return (
    <div className="notice onboarding-notice" role="status" id={id} data-testid="onboarding-notice" data-reason={r}>
      <div>{t(`onboarding.${r}` as const)}</div>
      <div className="onboarding-next">{t('onboarding.nextStep')}</div>
    </div>
  );
}

/**
 * The page's primary "new request" action: a normal link when the account is ready, otherwise a disabled
 * button that points at the notice (a disabled primary action always has its reason on the page).
 */
export function NewRequestAction({ to, label }: { to: string; label: string }): JSX.Element {
  const reason = useOnboardingReason();
  if (reason === 'READY') return <Link className="button-link primary" to={to}>{label}</Link>;
  return <button type="button" className="primary" disabled aria-disabled="true" aria-describedby="onboarding-notice">{label}</button>;
}
